import React from 'react';
import { TriageCategory } from '../../../contexts/triage/types';
import {
  faBan,
  faBell,
  faBox,
  faCircleNotch,
  faCommentSlash,
  faDotCircle,
  faFlagCheckered,
  faMoon,
  faPause,
  faTrash,
  IconDefinition
} from '@fortawesome/pro-solid-svg-icons';
import Badge from '../badge';
import Flag from '../../molecules/flag';
import Icon from '../../atoms/icon';
import Text from '../../atoms/text';
import Tooltip from '../tooltip';

type StatusBadgeCategories =
  | TriageCategory.Archived
  | TriageCategory.Canceled
  | TriageCategory.Completed
  | TriageCategory.Empty
  | TriageCategory.Inactive
  | TriageCategory.Paused
  | TriageCategory.Revoked
  | TriageCategory.Snoozed
  | TriageCategory.Unresponsive;

interface StatusBadgeCategoryConfig {
  icon: IconDefinition;
  bgClass?: string;
  textColor?: string;
  description: string;
  descriptionPatient?: string;
  wcag: string; // Required to meet WCAG standards
}
// TODO: Move this config into TriageCategory
const CONFIG: Record<StatusBadgeCategories, StatusBadgeCategoryConfig> = {
  [TriageCategory.Archived]: {
    icon: faBox,
    textColor: 'text-brand',
    description: 'Archived',
    wcag: ''
  },
  [TriageCategory.Canceled]: {
    icon: faTrash,
    textColor: 'text-brand',
    description: 'Program removed',
    descriptionPatient: 'All programs have been removed',
    wcag: 'Program Removed'
  },
  [TriageCategory.Completed]: {
    icon: faFlagCheckered,
    textColor: 'text-grey',
    description: "Program reached it's prescribed end date",
    descriptionPatient: 'All programs have reached their end dates',
    wcag: 'Program Ended'
  },
  [TriageCategory.Empty]: {
    icon: faDotCircle,
    textColor: 'text-grey',
    description: 'No responses received',
    wcag: 'No responses'
  },
  [TriageCategory.Inactive]: {
    icon: faCommentSlash,
    textColor: 'text-blue',
    description: 'Has not consented to get messages from this program',
    descriptionPatient: 'Has not consented to getting messages from CareSignal',
    wcag: 'Unconsented'
  },
  [TriageCategory.Paused]: {
    icon: faPause,
    bgClass: 'light-orange',
    textColor: 'text-brand',
    description: 'Service is paused',
    wcag: 'Paused'
  },
  [TriageCategory.Revoked]: {
    icon: faBan,
    textColor: 'text-blue',
    description: 'Opted out of program',
    wcag: 'Opted out'
  },
  [TriageCategory.Snoozed]: {
    icon: faMoon,
    bgClass: 'light-orange',
    textColor: 'text-brand',
    description: 'Snoozed',
    wcag: 'Snoozed'
  },
  [TriageCategory.Unresponsive]: {
    icon: faCircleNotch,
    textColor: 'text-blue',
    description: 'Less-engaged with program',
    descriptionPatient: 'Less-engaged',
    wcag: 'Less-engaged'
  }
};
const CONFIG_ALERT = {
  icon: faBell,
  title: '',
  wcag: ''
};
interface StatusBadgeProps {
  category?: TriageCategory;
  alert?: {
    total: number;
  };
  isPatient?: boolean;
  styles?: any;
}
const GeneratedBadge: React.FC<StatusBadgeProps> = ({
  category,
  alert,
  isPatient,
  styles
}) => {
  if (alert) {
    return (
      <Badge>
        <Flag bgColor="light-blue">
          <span role="status">
            <Icon
              title="alerts"
              className="mr-1 text-blue"
              icon={CONFIG_ALERT.icon}
            />
            <Text className="text-small font-bold">{alert.total}</Text>
          </span>
        </Flag>
      </Badge>
    );
  } else if (category) {
    const config = CONFIG[category];
    return (
      <Tooltip
        content={
          <React.Fragment>
            <Icon
              className="mr-3"
              color="white"
              icon={config.icon}
              title={config.wcag}
            />
            {isPatient && config.descriptionPatient
              ? config.descriptionPatient
              : config.description}
          </React.Fragment>
        }
      >
        <Badge>
          <Flag
            bgColor={
              isPatient
                ? 'white'
                : config.bgColor
                  ? config.bgColor
                  : 'light-grey'
            }
            styles={styles}
          >
            <Icon
              className={`mr-1 ${
                config.textColor ? config.textColor : 'text-blue'
              }`}
              icon={config.icon}
              title={config.wcag}
            />
          </Flag>
        </Badge>
      </Tooltip>
    );
  }
  return <div>FIXME</div>;
};
const StatusBadge: React.FC<StatusBadgeProps> = ({
  category,
  alert,
  isPatient,
  styles
}) => {
  return (
    <GeneratedBadge
      category={category}
      alert={alert}
      isPatient={isPatient}
      styles={styles}
    />
  );
};

export default StatusBadge;
